import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Accordion,
  Alert,
  Badge,
  Button,
  Card,
  Code,
  Divider,
  Grid,
  Group,
  Loader,
  Center,
  Stack,
  Text,
  Title,
  Tooltip,
} from '@mantine/core';
import {
  IconArrowLeft,
  IconAlertCircle,
  IconArrowDown,
  IconArrowUp,
  IconRefresh,
  IconSend,
} from '@tabler/icons-react';
import { disbursementsApi } from '../api/disbursements';
import type { ManualStatusUpdateResponse, TrxStatus } from '../api/types';
import { formatGmt7, formatIdr, statusColor } from '../lib/format';
import { DetailField } from '../components/DetailField';
import { JsonView } from '../components/JsonView';
import { ManualStatusControl } from '../components/ManualStatusControl';
import { ApiCallError, describeError } from '../api/client';
import { usePermissions, useSession } from '../auth/SessionContext';
import { isPlatform } from '../auth/permissions';
import { useTranslation } from '../i18n/LocaleContext';

type TFn = ReturnType<typeof useTranslation>['t'];

export function DisbursementDetailPage() {
  const { trxId = '' } = useParams<{ trxId: string }>();
  const { t } = useTranslation();
  const { has } = usePermissions();
  const { state: sessionState } = useSession();
  const queryClient = useQueryClient();
  const [resendNotice, setResendNotice] = useState<
    { kind: 'success'; deliveryId: string; targetUrl: string } | null
  >(null);
  const [statusNotice, setStatusNotice] = useState<ManualStatusUpdateResponse | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ['disbursement', trxId],
    queryFn: () => disbursementsApi.detail(trxId),
    enabled: trxId.length > 0,
    retry: 0,
  });
  const auditQuery = useQuery({
    queryKey: ['disbursement-audit-log', trxId],
    queryFn: () => disbursementsApi.apiLog(trxId),
    enabled: trxId.length > 0,
    retry: 0,
  });

  const canResend = has('webhook.delivery.retry') || has('webhook.delivery.internal.retry');

  const resendMut = useMutation({
    meta: {
      successKey: 'notify.webhook.resend.ok',
      errorKey: 'notify.webhook.resend.fail',
    },
    mutationFn: () => disbursementsApi.resendWebhook(trxId),
    onSuccess: (resp) => {
      setResendNotice({ kind: 'success', deliveryId: resp.deliveryId, targetUrl: resp.targetUrl });
      void queryClient.invalidateQueries({ queryKey: ['disbursement-audit-log', trxId] });
    },
  });

  // Manual status override: internal staff only (server enforces the same).
  const canUpdateStatus =
    sessionState.status === 'authenticated' &&
    isPlatform(sessionState.profile) &&
    has('disbursements.status.update');

  const statusMut = useMutation({
    meta: {
      successKey: 'notify.trx.status.ok',
      errorKey: 'notify.trx.status.fail',
    },
    mutationFn: (input: { status: TrxStatus; resendCallback: boolean }) =>
      disbursementsApi.updateStatus(trxId, input),
    onSuccess: (resp) => {
      setStatusNotice(resp);
      setResendNotice(null);
      void queryClient.invalidateQueries({ queryKey: ['disbursement', trxId] });
      void queryClient.invalidateQueries({ queryKey: ['disbursement-audit-log', trxId] });
      void queryClient.invalidateQueries({ queryKey: ['disbursements'] });
    },
  });

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="sm">
          <Button
            component={Link}
            to="/dashboard/disbursements"
            variant="default"
            leftSection={<IconArrowLeft size={14} />}
          >
            {t('common.back')}
          </Button>
          <Title order={3}>{t('disbursementDetail.title')}</Title>
        </Group>
        {(canUpdateStatus || canResend) && (
          <Group gap="sm" wrap="wrap">
            {canUpdateStatus && (
              <ManualStatusControl
                trxId={trxId}
                currentStatus={data?.transpay.trxStatus}
                loading={statusMut.isPending}
                onConfirm={(input) => statusMut.mutate(input)}
              />
            )}
            {/* Separator: the manual override (moves money) is a different
                kind of action from re-sending an already-emitted webhook. */}
            {canUpdateStatus && canResend && <Divider orientation="vertical" />}
            {canResend && (
              <Tooltip
                label={
                  data?.transpay.trxStatus === 'paid' || data?.transpay.trxStatus === 'failed'
                    ? t('disbursementDetail.resendTooltipReady')
                    : t('disbursementDetail.resendTooltipNotReady')
                }
              >
                <Button
                  variant="default"
                  leftSection={<IconSend size={14} />}
                  onClick={() => resendMut.mutate()}
                  loading={resendMut.isPending}
                  disabled={
                    !data ||
                    (data.transpay.trxStatus !== 'paid' && data.transpay.trxStatus !== 'failed')
                  }
                >
                  {t('disbursementDetail.resendButton')}
                </Button>
              </Tooltip>
            )}
          </Group>
        )}
      </Group>

      {statusNotice && (
        <Alert
          color={statusNotice.callbackRequested && !statusNotice.callbackQueued ? 'yellow' : 'green'}
          icon={<IconAlertCircle size={16} />}
          withCloseButton
          onClose={() => setStatusNotice(null)}
          variant="light"
        >
          {t('trxStatus.noticeChanged', {
            from: statusNotice.previousStatus,
            to: statusNotice.status,
          })}{' '}
          {statusNotice.callbackQueued
            ? t('trxStatus.noticeCallbackQueued', {
                targetUrl: statusNotice.targetUrl ?? '',
                outboxId: statusNotice.outboxId ?? '',
              })
            : statusNotice.callbackSkippedReason === 'no_event_for_pending'
              ? t('trxStatus.noticeCallbackSkippedPending')
              : statusNotice.callbackSkippedReason === 'webhook_url_not_configured'
                ? t('trxStatus.noticeCallbackSkippedNoUrl')
                : t('trxStatus.noticeCallbackNotRequested')}
        </Alert>
      )}

      {resendNotice && (
        <Alert
          color="green"
          icon={<IconAlertCircle size={16} />}
          withCloseButton
          onClose={() => setResendNotice(null)}
          variant="light"
        >
          {t('disbursementDetail.resendNotice', {
            targetUrl: resendNotice.targetUrl,
            deliveryId: resendNotice.deliveryId,
          })}
        </Alert>
      )}

      {isLoading && (
        <Center py="xl">
          <Loader />
        </Center>
      )}

      {error && (
        <Alert color="red" icon={<IconAlertCircle size={16} />}>
          {describeError(error)}
        </Alert>
      )}

      {data && (
        <Card>
          <Stack gap="md">
            <Group justify="space-between">
              <Group gap="sm">
                <Badge color={statusColor(data.transpay.trxStatus)} variant="filled" size="lg">
                  {data.transpay.trxStatus}
                </Badge>
                <Text size="sm" ff="monospace" c="dimmed">
                  {data.transpay.trxId}
                </Text>
              </Group>
              <Text fw={700} size="xl">
                {formatIdr(data.transpay.amount)}
              </Text>
            </Group>

            <Divider />

            <Grid>
              <Grid.Col span={{ base: 6, md: 4 }}>
                <DetailField label={t('disbursementDetail.fieldReference')}>{data.transpay.referenceId}</DetailField>
              </Grid.Col>
              <Grid.Col span={{ base: 6, md: 4 }}>
                <DetailField label={t('disbursementDetail.fieldMerchant')}>{data.transpay.merchantName}</DetailField>
              </Grid.Col>
              <Grid.Col span={{ base: 6, md: 4 }}>
                <DetailField label={t('disbursementDetail.fieldMethod')}>{data.transpay.method}</DetailField>
              </Grid.Col>
              <Grid.Col span={{ base: 6, md: 4 }}>
                <DetailField label={t('disbursementDetail.fieldBank')}>
                  {data.transpay.beneficiary.bankName ?? data.transpay.beneficiary.bankCode ?? '—'}
                </DetailField>
              </Grid.Col>
              <Grid.Col span={{ base: 6, md: 4 }}>
                <DetailField label={t('disbursementDetail.fieldAccountNumber')}>
                  <Code>{data.transpay.beneficiary.accountNumber ?? '—'}</Code>
                </DetailField>
              </Grid.Col>
              <Grid.Col span={{ base: 6, md: 4 }}>
                <DetailField label={t('disbursementDetail.fieldHolderName')}>{data.transpay.beneficiary.holderName ?? '—'}</DetailField>
              </Grid.Col>
              <Grid.Col span={{ base: 6, md: 4 }}>
                <DetailField label={t('disbursementDetail.fieldFee')}>{formatIdr(data.transpay.fee)}</DetailField>
              </Grid.Col>
              <Grid.Col span={{ base: 6, md: 4 }}>
                <DetailField label={t('disbursementDetail.fieldTotalDebit')}>{formatIdr(data.transpay.amount + data.transpay.fee)}</DetailField>
              </Grid.Col>
              <Grid.Col span={{ base: 6, md: 4 }}>
                <DetailField label={t('disbursementDetail.fieldRrn')}>{data.transpay.rrnNumber ?? '—'}</DetailField>
              </Grid.Col>
              <Grid.Col span={{ base: 6, md: 4 }}>
                <DetailField label={t('disbursementDetail.fieldCreated')}>{formatGmt7(data.transpay.createdAt)}</DetailField>
              </Grid.Col>
              <Grid.Col span={{ base: 6, md: 4 }}>
                <DetailField label={t('disbursementDetail.fieldPaid')}>
                  {data.transpay.paidAt ? formatGmt7(data.transpay.paidAt) : '—'}
                </DetailField>
              </Grid.Col>
              <Grid.Col span={{ base: 6, md: 4 }}>
                <DetailField label={t('disbursementDetail.fieldCreatedBy')}>{data.transpay.createdBy}</DetailField>
              </Grid.Col>
              {data.transpay.remark && (
                <Grid.Col span={12}>
                  <DetailField label={t('disbursementDetail.fieldRemark')}>{data.transpay.remark}</DetailField>
                </Grid.Col>
              )}
            </Grid>

            {data.payok && (
              <>
                <Divider label={t('disbursementDetail.upstreamProvider')} />
                <Grid>
                  <Grid.Col span={{ base: 6, md: 4 }}>
                    <DetailField label={t('disbursementDetail.fieldMerchantOrderId')}>
                      <Code style={{ fontSize: 11 }}>{data.payok.merchantOrderId}</Code>
                    </DetailField>
                  </Grid.Col>
                  {/* Platform Order ID = upstream provider's order ID (populated
                      after a successful upstream create call). Hidden when null
                      to avoid showing an empty placeholder row. */}
                  {data.payok.platformOrderId && (
                    <Grid.Col span={{ base: 6, md: 4 }}>
                      <DetailField label={t('disbursementDetail.fieldUpstreamOrderId')}>
                        <Code style={{ fontSize: 11 }}>{data.payok.platformOrderId}</Code>
                      </DetailField>
                    </Grid.Col>
                  )}
                  {data.payok.inquiryToken && (
                    <Grid.Col span={{ base: 6, md: 4 }}>
                      <DetailField label={t('disbursementDetail.fieldInquiryToken')}>
                        <Code style={{ fontSize: 11 }}>{data.payok.inquiryToken}</Code>
                      </DetailField>
                    </Grid.Col>
                  )}
                </Grid>
              </>
            )}
          </Stack>
        </Card>
      )}

      {data && (
        <ApiActivityCard
          trxId={trxId}
          loading={auditQuery.isLoading}
          error={auditQuery.error}
          rows={auditQuery.data?.rows ?? []}
          canSeePayok={auditQuery.data?.canSeePayok ?? false}
          onRefresh={() => auditQuery.refetch()}
          refreshing={auditQuery.isRefetching}
          payokMerchantOrderId={data.payok?.merchantOrderId ?? null}
          payokPlatformOrderId={data.payok?.platformOrderId ?? null}
        />
      )}
    </Stack>
  );
}

// ===========================================================================
// API Activity — same visual structure as DepositDetail.
//
// Differences for disbursement:
//   - "Initial Request" can contain MULTIPLE inbound + outbound rows
//     (inquiry → /api/disbursements/inquiry inbound + PAYOK inquiry outbound,
//      create  → /api/disbursements inbound + PAYOK order/create outbound).
//   - The rest (notification cycles, timeline entries) is identical.
// ===========================================================================

type AuditEntry = {
  id: number;
  direction: 'inbound' | 'outbound';
  party: 'merchant' | 'payok' | 'self';
  method: string;
  url: string;
  requestHeaders: unknown;
  requestBody: unknown;
  responseStatus: number | null;
  responseHeaders: unknown;
  responseBody: unknown;
  durationMs: number | null;
  error: string | null;
  startedAt: string;
  completedAt: string | null;
  synthesised?: boolean;
};

type LifecycleData = {
  initial: {
    /** Multiple inbound rows for disbursement: inquiry call + create call. */
    inbound: AuditEntry[];
    /** Multiple outbound rows: PAYOK inquiry + order/create. */
    outbound: AuditEntry[];
  };
  cycles: Array<{
    label: string;
    callback: AuditEntry | null;
    webhookAttempts: AuditEntry[];
  }>;
  orphans: AuditEntry[];
};

function organiseLifecycle(
  rows: readonly AuditEntry[],
  canSeePayok: boolean,
): LifecycleData {
  const asc = [...rows].sort(
    (a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
  );
  const payokCallbacks = asc.filter((r) => r.direction === 'inbound' && r.party === 'payok');
  const firstCallbackAt = payokCallbacks[0]
    ? new Date(payokCallbacks[0].startedAt).getTime()
    : Infinity;

  const inboundMerchant: AuditEntry[] = [];
  const outboundPayok: AuditEntry[] = [];
  const merchantWebhooks: AuditEntry[] = [];
  for (const r of asc) {
    const t = new Date(r.startedAt).getTime();
    if (r.direction === 'inbound' && r.party === 'merchant' && t < firstCallbackAt) {
      inboundMerchant.push(r);
    } else if (r.direction === 'outbound' && r.party === 'payok') {
      outboundPayok.push(r);
    } else if (r.direction === 'outbound' && r.party === 'merchant') {
      merchantWebhooks.push(r);
    }
  }

  // Group merchant webhooks by their preceding callback.
  let cycles: LifecycleData['cycles'];
  if (canSeePayok && payokCallbacks.length > 0) {
    cycles = payokCallbacks.map((callback, idx) => ({
      label: `Cycle #${idx + 1}`,
      callback,
      webhookAttempts: [] as AuditEntry[],
    }));
    for (const wh of merchantWebhooks) {
      const whT = new Date(wh.startedAt).getTime();
      let cycleIdx = -1;
      for (let i = 0; i < payokCallbacks.length; i += 1) {
        const cb = payokCallbacks[i]!;
        if (new Date(cb.startedAt).getTime() <= whT) cycleIdx = i;
        else break;
      }
      if (cycleIdx >= 0) cycles[cycleIdx]!.webhookAttempts.push(wh);
      else cycles[0]!.webhookAttempts.push(wh);
    }
  } else {
    // External viewer: bucket webhooks by delivery id (one notification per id).
    const byDelivery = new Map<string, AuditEntry[]>();
    const unknown: AuditEntry[] = [];
    for (const wh of merchantWebhooks) {
      const id = getDeliveryId(wh);
      if (id == null) {
        unknown.push(wh);
        continue;
      }
      const bucket = byDelivery.get(id) ?? [];
      bucket.push(wh);
      byDelivery.set(id, bucket);
    }
    const orderedKeys = [...byDelivery.keys()].sort((a, b) =>
      new Date(byDelivery.get(a)![0]!.startedAt).getTime() -
      new Date(byDelivery.get(b)![0]!.startedAt).getTime(),
    );
    cycles = orderedKeys.map((id, idx) => ({
      label: `Notification #${idx + 1}`,
      callback: null,
      webhookAttempts: byDelivery.get(id)!,
    }));
    if (unknown.length > 0) {
      cycles.push({
        label: `Notification #${cycles.length + 1}`,
        callback: null,
        webhookAttempts: unknown,
      });
    }
  }

  return {
    initial: {
      inbound: inboundMerchant,
      outbound: canSeePayok ? outboundPayok : [],
    },
    cycles,
    orphans: [],
  };
}

function getDeliveryId(entry: AuditEntry): string | null {
  const headers = entry.requestHeaders as Record<string, unknown> | null;
  if (!headers) return null;
  const v = headers['x-transpay-delivery-id'];
  if (typeof v === 'string' && v.length > 0) return v;
  if (Array.isArray(v) && typeof v[0] === 'string') return v[0];
  return null;
}

function durationColor(ms: number | null): string {
  if (ms == null) return 'gray';
  if (ms < 200) return 'green';
  if (ms < 500) return 'blue';
  if (ms < 2000) return 'yellow';
  if (ms < 5000) return 'orange';
  return 'red';
}

function minStartedAt(rows: readonly AuditEntry[]): string {
  let min = rows[0]!.startedAt;
  for (const r of rows) if (r.startedAt < min) min = r.startedAt;
  return min;
}

/** Pretty label per route — fallback to the URL for unknown ones. */
function endpointLabel(entry: AuditEntry, t: TFn): string {
  const u = entry.url;
  if (entry.party === 'merchant' && entry.direction === 'inbound') {
    if (u.includes('/inquiry')) return t('apiActivity.labelMerchantInquiry');
    if (u.includes('/disbursements') && !u.includes('inquiry')) {
      return t('apiActivity.labelMerchantOrderCreate');
    }
    return t('apiActivity.labelMerchantToTranspay');
  }
  if (entry.party === 'payok' && entry.direction === 'outbound') {
    if (u.includes('account/inquiry')) return t('apiActivity.labelPayokInquiry');
    if (u.includes('order/create')) return t('apiActivity.labelPayokOrderCreate');
    if (u.includes('order/query')) return t('apiActivity.labelPayokOrderQuery');
    return t('apiActivity.labelTranspayToPayok');
  }
  if (entry.party === 'payok' && entry.direction === 'inbound') {
    return t('apiActivity.labelPayokCallback');
  }
  if (entry.party === 'merchant' && entry.direction === 'outbound') {
    return t('apiActivity.labelMerchantWebhook');
  }
  return entry.url;
}

function ApiActivityCard({
  trxId,
  loading,
  error,
  rows,
  canSeePayok,
  onRefresh,
  refreshing,
  payokMerchantOrderId,
  payokPlatformOrderId,
}: {
  trxId: string;
  loading: boolean;
  error: unknown;
  rows: readonly AuditEntry[];
  canSeePayok: boolean;
  onRefresh: () => void;
  refreshing: boolean;
  payokMerchantOrderId: string | null;
  payokPlatformOrderId: string | null;
}) {
  const { t } = useTranslation();
  if (error instanceof ApiCallError && error.code === 'permission_denied') {
    return null;
  }
  const lifecycle = rows.length > 0 ? organiseLifecycle(rows, canSeePayok) : null;
  const firstStartedAt = rows.length > 0 ? minStartedAt(rows) : null;

  return (
    <Card>
      <Stack gap="md">
        <Group justify="space-between" align="flex-start">
          <div>
            <Title order={4}>{t('apiActivity.title')}</Title>
            <Text
              size="xs"
              c="dimmed"
              dangerouslySetInnerHTML={{
                __html: canSeePayok
                  ? t('apiActivity.descInternalDisbursement')
                  : t('apiActivity.descExternalDisbursement'),
              }}
            />
          </div>
          <Button
            size="xs"
            variant="default"
            leftSection={<IconRefresh size={14} />}
            loading={refreshing}
            onClick={onRefresh}
          >
            {t('apiActivity.refresh')}
          </Button>
        </Group>

        {canSeePayok && (payokMerchantOrderId || payokPlatformOrderId) && (
          <Group gap="xl">
            {payokMerchantOrderId && (
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('apiActivity.upstreamMerchantOrderId')}</Text>
                <Text size="xs" ff="monospace">{payokMerchantOrderId}</Text>
              </div>
            )}
            {payokPlatformOrderId && (
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('apiActivity.upstreamPlatformOrderId')}</Text>
                <Text size="xs" ff="monospace">{payokPlatformOrderId}</Text>
              </div>
            )}
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('apiActivity.trxId')}</Text>
              <Text size="xs" ff="monospace">{trxId}</Text>
            </div>
          </Group>
        )}
        {!canSeePayok && (
          <Group gap="xl">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('apiActivity.trxId')}</Text>
              <Text size="xs" ff="monospace">{trxId}</Text>
            </div>
          </Group>
        )}

        <Group gap="xs">
          <Badge color="brand" variant="light">{t('apiActivity.merchantRowsVisible')}</Badge>
          {canSeePayok && (
            <Badge color="grape" variant="light">{t('apiActivity.upstreamRowsVisible')}</Badge>
          )}
          <Text size="xs" c="dimmed">{t('apiActivity.entryCount', { n: rows.length })}</Text>
        </Group>

        {loading && <Center py="md"><Loader size="sm" /></Center>}

        {Boolean(error) && !(error instanceof ApiCallError && error.code === 'permission_denied') && (
          <Alert color="red" icon={<IconAlertCircle size={16} />}>
            {describeError(error, t('apiActivity.loadFailed'))}
          </Alert>
        )}

        {rows.length === 0 && !loading && !error && (
          <Text c="dimmed" ta="center" py="md">{t('apiActivity.emptyDisbursement')}</Text>
        )}

        {lifecycle && (lifecycle.initial.inbound.length > 0 || lifecycle.initial.outbound.length > 0) && (
          <InitialRequestCard
            inbound={lifecycle.initial.inbound}
            outbound={lifecycle.initial.outbound}
            firstStartedAt={firstStartedAt}
            canSeePayok={canSeePayok}
          />
        )}

        {lifecycle && lifecycle.cycles.length > 0 && (
          <Stack gap="sm">
            <Text fw={600} size="sm" tt="uppercase" c="dimmed">
              {canSeePayok ? t('apiActivity.notificationCycles') : t('apiActivity.webhookNotifications')} ·{' '}
              {lifecycle.cycles.length}
            </Text>
            {lifecycle.cycles.map((cycle, idx) => (
              <CycleCard
                key={cycle.callback?.id ?? `delivery-${idx}`}
                cycle={cycle}
                index={idx + 1}
                firstStartedAt={firstStartedAt}
                canSeePayok={canSeePayok}
              />
            ))}
          </Stack>
        )}
      </Stack>
    </Card>
  );
}

function InitialRequestCard({
  inbound,
  outbound,
  firstStartedAt,
  canSeePayok,
}: {
  inbound: AuditEntry[];
  outbound: AuditEntry[];
  firstStartedAt: string | null;
  canSeePayok: boolean;
}) {
  const { t } = useTranslation();
  // Interleave in chronological order so the reader walks the flow.
  const all = [...inbound, ...outbound].sort(
    (a, b) => new Date(a.startedAt).getTime() - new Date(b.startedAt).getTime(),
  );
  const subtitle = canSeePayok
    ? t('apiActivity.initialSubtitleInternalDisbursement', { inbound: inbound.length, outbound: outbound.length })
    : t('apiActivity.initialSubtitleExternalDisbursement', { inbound: inbound.length });
  return (
    <Card withBorder padding={0} radius="md">
      <Group
        justify="space-between"
        px="md"
        py="sm"
        style={{
          background:
            'light-dark(var(--mantine-color-brand-0), var(--mantine-color-brand-9))',
          borderBottom:
            '1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4))',
        }}
      >
        <Group gap="sm">
          <Badge color="brand" variant="filled" size="lg" radius="sm">
            {t('apiActivity.initialRequest')}
          </Badge>
          <Text size="sm" c="dimmed">{subtitle}</Text>
        </Group>
      </Group>
      <Accordion variant="default" multiple chevronPosition="left">
        {all.map((entry, idx) => (
          <TimelineEntry
            key={entry.id}
            entry={entry}
            index={idx + 1}
            total={all.length}
            itemNoun={endpointLabel(entry, t)}
            groupColor={entry.party === 'payok' ? 'grape' : 'brand'}
            firstStartedAt={firstStartedAt}
          />
        ))}
      </Accordion>
    </Card>
  );
}

function CycleCard({
  cycle,
  index,
  firstStartedAt,
  canSeePayok,
}: {
  cycle: { label: string; callback: AuditEntry | null; webhookAttempts: AuditEntry[] };
  index: number;
  firstStartedAt: string | null;
  canSeePayok: boolean;
}) {
  const { t } = useTranslation();
  const allEntries = [
    ...(cycle.callback ? [cycle.callback] : []),
    ...cycle.webhookAttempts,
  ];
  const summary = (() => {
    const hasErr = allEntries.some((e) => e.error != null);
    if (hasErr) return { color: 'red', label: t('apiActivity.summaryErrors') };
    const worst = allEntries.length > 0
      ? Math.max(...allEntries.map((e) => e.responseStatus ?? 0))
      : 0;
    const color = worst >= 500 ? 'red' : worst >= 400 ? 'orange' : worst >= 300 ? 'yellow' : 'green';
    return { color, label: t('apiActivity.summaryWorstHttp', { n: worst }) };
  })();
  const totalCount = allEntries.length;
  const headerBg = canSeePayok
    ? 'light-dark(var(--mantine-color-grape-0), var(--mantine-color-grape-9))'
    : 'light-dark(var(--mantine-color-brand-0), var(--mantine-color-brand-9))';
  const headerBadgeColor = canSeePayok ? 'grape' : 'brand';
  const headerTitle = canSeePayok ? t('apiActivity.cycleTitle', { n: index }) : t('apiActivity.notificationTitle', { n: index });
  const headerSubtitle = canSeePayok
    ? (cycle.webhookAttempts.length === 1
        ? t('apiActivity.cycleSubtitleOne', { n: cycle.webhookAttempts.length })
        : t('apiActivity.cycleSubtitleMany', { n: cycle.webhookAttempts.length }))
    : (cycle.webhookAttempts.length === 1
        ? t('apiActivity.notificationSubtitleOne', { n: cycle.webhookAttempts.length })
        : t('apiActivity.notificationSubtitleMany', { n: cycle.webhookAttempts.length }));

  return (
    <Card withBorder padding={0} radius="md">
      <Group
        justify="space-between"
        px="md"
        py="sm"
        style={{
          background: headerBg,
          borderBottom:
            '1px solid light-dark(var(--mantine-color-gray-2), var(--mantine-color-dark-4))',
        }}
      >
        <Group gap="sm">
          <Badge color={headerBadgeColor} variant="filled" size="lg" radius="sm">
            {headerTitle}
          </Badge>
          <Text size="sm" c="dimmed">{headerSubtitle}</Text>
        </Group>
        <Group gap={6}>
          <Badge color={summary.color} variant="light">{summary.label}</Badge>
          <Text size="xs" c="dimmed">{t('apiActivity.cycleEntries', { n: totalCount })}</Text>
        </Group>
      </Group>
      <Accordion variant="default" multiple chevronPosition="left">
        {canSeePayok && cycle.callback && (
          <TimelineEntry
            entry={cycle.callback}
            index={1}
            total={totalCount}
            itemNoun={t('apiActivity.upstreamCallbackNoun')}
            groupColor="grape"
            firstStartedAt={firstStartedAt}
          />
        )}
        {cycle.webhookAttempts.map((wh, idx) => (
          <TimelineEntry
            key={wh.id}
            entry={wh}
            index={idx + 1 + (canSeePayok && cycle.callback ? 1 : 0)}
            total={totalCount}
            itemNoun={t('apiActivity.webhookAttemptNoun', { n: idx + 1 })}
            groupColor="brand"
            firstStartedAt={firstStartedAt}
          />
        ))}
      </Accordion>
    </Card>
  );
}

function TimelineEntry({
  entry,
  index,
  total,
  itemNoun,
  groupColor,
  firstStartedAt,
}: {
  entry: AuditEntry;
  index: number;
  total: number;
  itemNoun: string;
  groupColor: string;
  firstStartedAt: string | null;
}) {
  const { t } = useTranslation();
  const isInbound = entry.direction === 'inbound';
  const dirIcon = isInbound ? <IconArrowDown size={14} /> : <IconArrowUp size={14} />;

  const statusBadge = (() => {
    if (entry.error) return <Badge color="red" variant="filled" size="sm">{t('apiActivity.statusErr')}</Badge>;
    if (entry.responseStatus == null) return <Badge color="gray" variant="filled" size="sm">—</Badge>;
    const s = entry.responseStatus;
    const color = s >= 500 ? 'red' : s >= 400 ? 'orange' : s >= 300 ? 'yellow' : 'green';
    return <Badge color={color} variant="filled" size="sm">{s}</Badge>;
  })();

  const relative = (() => {
    if (!firstStartedAt) return '';
    const delta = new Date(entry.startedAt).getTime() - new Date(firstStartedAt).getTime();
    if (delta === 0) return 't+0s';
    if (delta < 1000) return `t+${delta}ms`;
    if (delta < 60_000) return `t+${(delta / 1000).toFixed(1)}s`;
    if (delta < 3600_000) return `t+${(delta / 60_000).toFixed(1)}min`;
    return `t+${(delta / 3600_000).toFixed(1)}h`;
  })();

  const dColor = durationColor(entry.durationMs);

  return (
    <Accordion.Item value={String(entry.id)}>
      <Accordion.Control>
        <Group gap="md" wrap="nowrap" align="center">
          <Badge color={groupColor} variant="outline" size="lg" radius="sm">
            #{index}{total > 1 ? ` / ${total}` : ''}
          </Badge>
          <Group gap={4} wrap="nowrap" style={{ minWidth: 150 }}>
            {dirIcon}
            <Text size="xs" fw={500}>{itemNoun}</Text>
          </Group>
          <Text size="xs" ff="monospace" style={{ flex: 1, minWidth: 0 }} truncate c="dimmed">
            {entry.method} {entry.url}
          </Text>
          {entry.synthesised && (
            <Tooltip label={t('apiActivity.legacyTooltipDisbursement')}>
              <Badge color="yellow" variant="dot" size="xs">{t('apiActivity.reconstructedRowBadge')}</Badge>
            </Tooltip>
          )}
          {statusBadge}
          <Tooltip
            label={
              isInbound
                ? t('apiActivity.durationTooltipInbound')
                : t('apiActivity.durationTooltipOutbound')
            }
          >
            <Badge color={dColor} variant="dot" size="md">
              {entry.durationMs != null ? `${entry.durationMs} ms` : t('apiActivity.noTiming')}
            </Badge>
          </Tooltip>
          <Text size="xs" c="dimmed" ff="monospace" style={{ minWidth: 60 }}>{relative}</Text>
          <Text size="xs" c="dimmed">{formatGmt7(entry.startedAt)}</Text>
        </Group>
      </Accordion.Control>
      <Accordion.Panel>
        <Stack gap="sm">
          {entry.error && (
            <Alert color="red" icon={<IconAlertCircle size={16} />}>
              {entry.error}
            </Alert>
          )}
          <Group gap="xl">
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('apiActivity.started')}</Text>
              <Text size="xs" ff="monospace">{formatGmt7(entry.startedAt)}</Text>
            </div>
            {entry.completedAt && (
              <div>
                <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('apiActivity.completed')}</Text>
                <Text size="xs" ff="monospace">{formatGmt7(entry.completedAt)}</Text>
              </div>
            )}
            <div>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{t('apiActivity.responseTime')}</Text>
              <Group gap={6}>
                <Text size="sm" ff="monospace" fw={600} c={`${durationColor(entry.durationMs)}.7`}>
                  {entry.durationMs != null ? `${entry.durationMs} ms` : '—'}
                </Text>
                {entry.responseStatus != null && (
                  <Text size="xs" c="dimmed">@ HTTP {entry.responseStatus}</Text>
                )}
              </Group>
            </div>
          </Group>
          <Grid>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>{t('apiActivity.request')}</Text>
              {entry.requestHeaders != null && (
                <>
                  <Text size="xs" c="dimmed">{t('apiActivity.headers')}</Text>
                  <JsonView value={entry.requestHeaders} maxHeight={160} />
                </>
              )}
              <Text size="xs" c="dimmed" mt={6}>{t('apiActivity.body')}</Text>
              <JsonView value={entry.requestBody} maxHeight={300} />
            </Grid.Col>
            <Grid.Col span={{ base: 12, md: 6 }}>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} mb={4}>
                {t('apiActivity.response')}{' '}
                {entry.responseStatus != null && (
                  <Text span size="xs" c="dimmed" ff="monospace">HTTP {entry.responseStatus}</Text>
                )}
              </Text>
              {entry.responseHeaders != null && (
                <>
                  <Text size="xs" c="dimmed">{t('apiActivity.headers')}</Text>
                  <JsonView value={entry.responseHeaders} maxHeight={160} />
                </>
              )}
              <Text size="xs" c="dimmed" mt={6}>{t('apiActivity.body')}</Text>
              <JsonView value={entry.responseBody} maxHeight={300} />
            </Grid.Col>
          </Grid>
        </Stack>
      </Accordion.Panel>
    </Accordion.Item>
  );
}
